import React from "react";

import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import type { BusbudError } from "@busbud/busbud-errors";

import { useAppContext } from "@app/helpers/hooks";
import { removeOutboundSearchState } from "@app/helpers/search";
import { removeCartItem } from "@app/modules/checkout/store/v2/thunks/cart-items";
import { removeDiscountCode } from "@app/modules/checkout/store/v2/thunks/discount";
import { unredeemCredits } from "@app/modules/checkout/store/v2/thunks/loyalty-rewards";
import type { CheckoutStep } from "@app/types/checkout-types";
import type { CheckoutReduxStateV2 } from "@app/types/checkout-v2-types";

import type { ErrorModalButtonType } from "../../../helpers/api-error";
import type { Dispatch } from "@reduxjs/toolkit";
import type { ConnectedProps } from "react-redux";

import * as ApiError from "../../../helpers/api-error";
import {
  closeModal,
  updatePaymentType
} from "../../../modules/checkout/actions";
import { CHECKOUT_STEPS } from "../../../modules/checkout/constants";
import * as ErrorTracking from "../../../tracking/error-tracking";
import * as WindowUtils from "../../../utils/window";
import { AlertDialog } from "../../alert-dialog";

const ERROR_HANDLING_ACTIONS = ApiError.ERROR_HANDLING_ACTIONS;

interface Props extends ConnectedProps<typeof connector> {
  error: BusbudError | null;
  checkout_step: CheckoutStep;
  onPassengerSectionSubmit: () => void;
  onDismiss?: () => void;
}

const CheckoutErrorModalComponent: React.FC<Props> = props => {
  const { api, locale, urlBuilder, tracker, translator, sentry } =
    useAppContext();
  const { cart_id, error, search, cart_item_id } = props;

  const defaultAction = () => {
    props.onDismiss && props.onDismiss();
    props.closeModal();
  };

  const handleAction = (
    action_name: string | null | undefined,
    error_button_type: ErrorModalButtonType
  ) => {
    props.onDismiss && props.onDismiss();

    let actionFunction: () => void;
    switch (action_name) {
      case ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH:
        actionFunction = () => {
          WindowUtils.redirect(urlBuilder.results(search));
        };
        break;
      case ERROR_HANDLING_ACTIONS.CHECKOUT_TRY_CREDIT_CARD:
      case ERROR_HANDLING_ACTIONS.CHECKOUT_CHANGE_CREDIT_CARD:
        actionFunction = () => {
          if (props.checkout_step !== CHECKOUT_STEPS.PAYMENT) {
            return props.onPassengerSectionSubmit();
          }
          props.closeModal();
        };
        break;
      case ERROR_HANDLING_ACTIONS.CHECKOUT_TRY_LATER:
        actionFunction = () => {};
        break;
      case ERROR_HANDLING_ACTIONS.CHECKOUT_MORE_INFO:
        actionFunction = () => {
          WindowUtils.open(
            `https://help.busbud.com/hc/${locale.slice(
              0,
              2
            )}/articles/115005328963`,
            "_blank",
            true
          );
        };
        break;
      case ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_HES:
      case ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_LOYALTY:
      case ERROR_HANDLING_ACTIONS.CHECKOUT_UPDATE_SEAT:
        actionFunction = () => {
          WindowUtils.redirect(
            urlBuilder.asyncCartWithCartCreated(
              removeOutboundSearchState(search),
              cart_id
            )
          );
        };
        break;
      case ERROR_HANDLING_ACTIONS.RESET_DISCOUNT_CODE:
        actionFunction = () => {
          props.removeDiscountCode({
            api,
            discount_code: search.discount_code!
          });
          props.closeModal();
        };
        break;
      case ERROR_HANDLING_ACTIONS.RESET_REDEEMED_AMOUNT:
        actionFunction = () => {
          props.unredeemCredits({ api, tracker });
          props.closeModal();
        };
        break;
      case ERROR_HANDLING_ACTIONS.RESET_ANCILLARY_ITEMS:
        actionFunction = () => {
          if (!cart_item_id) {
            return;
          }
          props.removeCartItem({
            api,
            cart_item_id
          });
          props.closeModal();
        };
        break;
      default:
        actionFunction = () => {
          props.closeModal();
        };
    }

    tracker.track(ErrorTracking.clickedErrorButton(error_button_type));
    actionFunction();
  };

  const onAction = (
    button_type: ErrorModalButtonType,
    action_name: string | null | undefined
  ) => {
    const use_handle_action =
      action_name === ERROR_HANDLING_ACTIONS.RESET_ANCILLARY_ITEMS ||
      button_type === "cta";

    return use_handle_action
      ? handleAction(action_name, button_type)
      : defaultAction();
  };

  const _getErrorModalOptions = () => {
    const error_modal_config = ApiError.getErrorModalConfig(
      translator,
      sentry,
      error,
      "en"
    );

    return error_modal_config;
  };

  const config = _getErrorModalOptions();
  const buttons = config.button_config || {
    primary_button: {
      label: translator.t("!route.errors.default-button-label")
    }
  };

  return (
    <AlertDialog
      title={config.title}
      message={config.text}
      tip={config.tip}
      actionHandler={onAction}
      onClose={defaultAction}
      {...buttons}
    />
  );
};

const mapStateToProps = (state: CheckoutReduxStateV2) => {
  return {
    cart_id: state.cart.id,
    search: state.search,
    cart_item_id: state.cart_items.value && state.cart_items.value[0]?.id
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      closeModal,
      updatePaymentType,
      removeDiscountCode,
      unredeemCredits,
      removeCartItem
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);

export const CheckoutErrorModal = connector(CheckoutErrorModalComponent);
